import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { OPCIONES_GOOGLE_MAPS } from '../../mapas/opcionesGoogleMaps';
import { Button } from 'primereact/button';
import { colorEvento, NIVELES } from './seguridadVisual';

/**
 * El viaje en el mapa: el recorrido, cada alerta numerada en el punto donde
 * ocurrio, y las paradas repetidas en el mismo lugar marcadas con un circulo.
 * Al tocar un evento se abre una ventana con su detalle y el boton para ver la
 * alerta completa.
 *
 * Mismo cargador y misma clave que MapaAlerta (id 'gmaps-alertas'), y las capas
 * se dibujan a mano por la misma razon: con React 18 en StrictMode los
 * componentes <Marker>/<Polyline> de la libreria quedan sin enganchar.
 */


const VISTAS = [
    { id: 'roadmap', label: 'Mapa', icono: 'pi-map' },
    { id: 'hybrid', label: 'Satélite', icono: 'pi-globe' }
];

const COLOR_TRAMO = { VACIO: '#7F77DD', LLENO: '#378ADD', VIAJE: '#378ADD' };

/** Ecuador continental: lo que cae afuera es un punto basura del equipo. */
const enEcuador = (lat, lng) => lat > -6 && lat < 2 && lng > -82 && lng < -74;

const valido = (lat, lng) => lat != null && lng != null && !isNaN(Number(lat)) && !isNaN(Number(lng))
    && Number(lat) !== 0 && Number(lng) !== 0 && enEcuador(Number(lat), Number(lng));

const fechaCorta = (v) => {
    if (!v) return '';
    const d = new Date(v);
    return isNaN(d.getTime()) ? '' : d.toLocaleString('es-EC', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
    });
};

/** Contenido de la ventana del evento, armado con nodos (nunca con HTML de texto). */
const ventana = (e, onVerDetalle) => {
    const div = document.createElement('div');
    div.style.cssText = 'font-family:inherit;font-size:13px;line-height:1.45;max-width:260px';
    const linea = (txt, estilo) => {
        if (!txt) return;
        const p = document.createElement('div');
        p.textContent = txt;
        if (estilo) p.style.cssText = estilo;
        div.appendChild(p);
    };
    linea(`${e.orden} · ${e.evento || e.novedad || e.tipoEvento || 'Evento'}`, 'font-weight:600;margin-bottom:2px');
    linea(fechaCorta(e.fechaEvento));
    linea(e.clase === 'OPERACION' ? 'Operación (no es alerta)' : 'Alerta', `color:${e.clase === 'OPERACION' ? '#5f5e5a' : '#a32d2d'}`);
    linea(e.nota);
    linea(e.rfid ? `Tarjeta ${e.rfid}` : null);
    linea(e.puntoCercano, 'color:#5f5e5a');
    if (onVerDetalle) {
        const b = document.createElement('button');
        b.type = 'button';
        b.textContent = 'Ver detalle';
        b.style.cssText = 'margin-top:6px;padding:4px 10px;border:1px solid #185fa5;background:#fff;color:#185fa5;border-radius:6px;cursor:pointer';
        b.addEventListener('click', () => onVerDetalle(e));
        div.appendChild(b);
    }
    return div;
};

const MapaViajeAlertas = ({ recorrido, eventos, grupos, radioParadaM = 150, ocultarOperacion = false, onVerDetalle }) => {

    const [vista, setVista] = useState('roadmap');
    const [ampliado, setAmpliado] = useState(false);
    const [mapa, setMapa] = useState(null);
    const angosto = typeof window !== 'undefined' && window.matchMedia
        ? window.matchMedia('(max-width: 768px)').matches : false;

    const { isLoaded } = useJsApiLoader(OPCIONES_GOOGLE_MAPS);

    // La funcion del padre cambia en cada render. Si fuera dependencia del
    // efecto, el mapa se redibujaria (y se re-encuadraria, cerrando la ventana
    // abierta) cada vez que la pantalla se refresca.
    const verDetalle = useRef(onVerDetalle);
    verDetalle.current = onVerDetalle;

    const puntos = useMemo(() => ((recorrido && recorrido.puntos) || [])
        .filter((p) => valido(p.lat, p.lon))
        .map((p) => ({ lat: Number(p.lat), lng: Number(p.lon), tramo: p.tramo || 'VIAJE' })), [recorrido]);

    const visibles = useMemo(() => (eventos || [])
        .filter((e) => valido(e.latitud, e.longitud))
        .filter((e) => !ocultarOperacion || e.clase !== 'OPERACION'), [eventos, ocultarOperacion]);

    const sinNada = puntos.length === 0 && visibles.length === 0;

    useEffect(() => {
        if (!isLoaded || !window.google || !mapa) return undefined;
        const g = window.google.maps;
        const capas = [];
        const info = new g.InfoWindow();
        const limites = new g.LatLngBounds();

        if (puntos.length > 1) {
            // Un trazo por tramo: el VACIO en violeta y el LLENO en azul.
            const porTramo = {};
            puntos.forEach((p) => { (porTramo[p.tramo] = porTramo[p.tramo] || []).push(p); });
            Object.entries(porTramo).forEach(([tramo, path]) => {
                if (path.length < 2) return;
                capas.push(new g.Polyline({
                    map: mapa, path, strokeColor: COLOR_TRAMO[tramo] || '#378ADD', strokeOpacity: 0.85,
                    strokeWeight: 4, clickable: false
                }));
            });
            puntos.forEach((p) => limites.extend(p));
            [[puntos[0], 'A', 'Inicio del recorrido'], [puntos[puntos.length - 1], 'B', 'Última posición']]
                .forEach(([pos, letra, titulo]) => capas.push(new g.Marker({
                    map: mapa, position: pos, title: titulo, zIndex: 500,
                    label: { text: letra, color: '#ffffff', fontSize: '11px', fontWeight: '600' },
                    icon: { path: g.SymbolPath.CIRCLE, scale: 9, fillColor: '#444441', fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 2 }
                })));
        }

        const destino = recorrido && recorrido.destino;
        if (destino && valido(destino.lat, destino.lon)) {
            const c = { lat: Number(destino.lat), lng: Number(destino.lon) };
            limites.extend(c);
            capas.push(new g.Circle({
                map: mapa, center: c, radius: recorrido.radioM || 200, clickable: false,
                strokeColor: '#3B6D11', strokeOpacity: 0.8, strokeWeight: 2, fillColor: '#97C459', fillOpacity: 0.12
            }));
            capas.push(new g.Marker({ map: mapa, position: c, title: `Destino: ${destino.nombre || ''}`, zIndex: 400 }));
        }

        (grupos || []).forEach((gr) => {
            if (!valido(gr.lat, gr.lon)) return;
            const nivel = NIVELES[gr.nivel] || NIVELES[0];
            capas.push(new g.Circle({
                map: mapa, center: { lat: Number(gr.lat), lng: Number(gr.lon) }, radius: radioParadaM, clickable: false,
                strokeColor: gr.lugarOperativo ? '#888780' : nivel.borde, strokeOpacity: 0.9, strokeWeight: 2,
                fillColor: gr.lugarOperativo ? '#B4B2A9' : nivel.color, fillOpacity: 0.18
            }));
        });

        visibles.forEach((e) => {
            const pos = { lat: Number(e.latitud), lng: Number(e.longitud) };
            limites.extend(pos);
            const color = colorEvento(e);
            const m = new g.Marker({
                map: mapa, position: pos, title: `${e.orden} · ${e.evento || e.novedad || ''}`,
                zIndex: e.clase === 'OPERACION' ? 600 : 800 + (e.orden || 0),
                label: { text: String(e.orden || ''), color: e.clase === 'OPERACION' ? '#2C2C2A' : '#ffffff', fontSize: '10px', fontWeight: '600' },
                icon: {
                    path: g.SymbolPath.CIRCLE, scale: 10, fillColor: color, fillOpacity: e.clase === 'OPERACION' ? 0.75 : 1,
                    strokeColor: '#ffffff', strokeWeight: 2
                }
            });
            m.addListener('click', () => {
                info.setContent(ventana(e, verDetalle.current ? (x) => verDetalle.current(x) : null));
                info.open({ map: mapa, anchor: m });
            });
            capas.push(m);
        });

        if (!limites.isEmpty()) {
            mapa.fitBounds(limites, 40);
        }
        return () => {
            info.close();
            capas.forEach((c) => c.setMap(null));
        };
    }, [isLoaded, mapa, puntos, visibles, grupos, recorrido, radioParadaM]);

    if (sinNada) {
        return (
            <div className="p-3 border-1 border-round surface-100 text-center text-sm text-color-secondary"
                 style={{ borderColor: 'var(--surface-border)' }}>
                <i className="pi pi-map-marker" style={{ fontSize: '1.3rem' }} />
                <div className="mt-2">Este viaje no tiene recorrido ni alertas con coordenada.</div>
            </div>
        );
    }

    return (
        <div className="border-1 border-round overflow-hidden" style={{ borderColor: 'var(--surface-border)' }}>
            <div className="flex align-items-center gap-1 p-2 surface-50 flex-wrap"
                 style={{ borderBottom: '1px solid var(--surface-border)' }}>
                {VISTAS.map((v) => (
                    <Button key={v.id} label={v.label} icon={`pi ${v.icono}`}
                            className={`p-button-sm ${vista === v.id ? '' : 'p-button-text'}`}
                            onClick={() => setVista(v.id)} />
                ))}
                <span className="ml-auto">
                    <Button icon={ampliado ? 'pi pi-window-minimize' : 'pi pi-window-maximize'}
                            label={ampliado ? 'Reducir' : 'Ampliar'}
                            className="p-button-sm p-button-text" onClick={() => setAmpliado(!ampliado)} />
                </span>
            </div>
            {!isLoaded ? (
                <div className="flex align-items-center justify-content-center surface-100" style={{ height: 340 }}>
                    <span className="text-color-secondary text-sm">Cargando el mapa…</span>
                </div>
            ) : (
                <GoogleMap
                    mapContainerStyle={{ width: '100%', height: ampliado ? '70vh' : 340 }}
                    center={puntos[0] || (visibles[0] && { lat: Number(visibles[0].latitud), lng: Number(visibles[0].longitud) })}
                    zoom={12}
                    onLoad={setMapa}
                    onUnmount={() => setMapa(null)}
                    mapTypeId={vista}
                    // En el celular un dedo desplaza la pagina y dos mueven el mapa: con
                    // 'greedy' el mapa se quedaba con todo el gesto y no se podia bajar
                    // a la lista de eventos.
                    options={{ gestureHandling: angosto ? 'cooperative' : 'greedy', clickableIcons: false, streetViewControl: false, mapTypeControl: false, fullscreenControl: false }} />
            )}
            <div className="flex flex-wrap gap-3 p-2 surface-50 text-xs text-color-secondary"
                 style={{ borderTop: '1px solid var(--surface-border)' }}>
                <span><span style={{ color: COLOR_TRAMO.VACIO }}>━━</span> vacío · <span style={{ color: COLOR_TRAMO.LLENO }}>━━</span> lleno ({puntos.length} posiciones)</span>
                <span><span style={{ color: '#E24B4A' }}>●</span> alerta</span>
                <span><span style={{ color: '#EF9F27' }}>●</span> parada</span>
                <span><span style={{ color: '#B4B2A9' }}>●</span> operación</span>
                <span>◯ paradas en el mismo lugar</span>
            </div>
        </div>
    );
};

export default MapaViajeAlertas;
